import { computed, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DashboardSummary, SalesTrendReport } from '@/services/reportsService'
import { useChartConfig } from './useChartConfig'

/**
 * Composable para datos de gráficas de reportes
 */
export function useReportsCharts(
  dashboard: Ref<DashboardSummary | null>,
  salesTrend: Ref<SalesTrendReport | null>
) {
  const { t } = useI18n()
  const { chartColors } = useChartConfig()

  /**
   * Datos para gráfica de productos más vendidos
   */
  const topProductsChartData = computed(() => {
    if (!dashboard.value) return { labels: [], datasets: [] }
    
    return {
      labels: dashboard.value.top_products.map(p => `${p.name} (${p.category_name})`),
      datasets: [
        {
          label: t('app.reports.quantity_sold'),
          data: dashboard.value.top_products.map(p => p.quantity_sold),
          backgroundColor: chartColors.primary,
          borderColor: chartColors.primaryBorder,
          borderWidth: 1
        }
      ]
    }
  })

  /**
   * Simple sequential color assignment for payment methods
   */
  const getPaymentMethodColor = (methodKey: string, index: number) => {
    const colors = [
      { bg: chartColors.green, border: chartColors.greenBorder },      // cash
      { bg: chartColors.blue, border: chartColors.blueBorder },        // card
      { bg: chartColors.purple, border: chartColors.purpleBorder },    // digital
      { bg: chartColors.orange, border: chartColors.orangeBorder },    // other
      { bg: '#fed7aa', border: '#fb923c' }, // Orange light
      { bg: '#fbcfe8', border: '#f472b6' }, // Pink light
      { bg: '#e0e7ff', border: '#6366f1' }, // Indigo light
      { bg: '#ccfbf1', border: '#14b8a6' }, // Teal light
      { bg: '#fef3c7', border: '#f59e0b' }, // Amber light
      { bg: '#dcfce7', border: '#22c55e' }  // Green light
    ]
    
    // Use index to get unique color for each method
    const colorIndex = index % colors.length
    return colors[colorIndex]
  }

  /**
   * Datos para gráfica de métodos de pago
   */
  const paymentChartData = computed(() => {
    if (!dashboard.value) return { labels: [], datasets: [] }
    
    const breakdown = dashboard.value.payment_breakdown
    const methodKeys = Object.keys(breakdown)
    const labels = methodKeys.map(key => {
      // Try to get translation for standard methods, otherwise use the key directly for custom methods
      const translationKey = `app.reports.payment_${key.toLowerCase()}`
      const translation = t(translationKey)
      // If translation returns the key itself (no translation found), use the original key
      return translation === translationKey ? key : translation
    })
    const data = Object.values(breakdown).map(v => v.amount)
    
    // Generate colors for each method - use sequential assignment
    const backgroundColors = methodKeys.map((key, index) => getPaymentMethodColor(key.toLowerCase(), index).bg)
    const borderColors = methodKeys.map((key, index) => getPaymentMethodColor(key.toLowerCase(), index).border)
    
        
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        }
      ]
    }
  })

  /**
   * Datos para gráfica de tendencia de ventas
   */
  const salesTrendChartData = computed(() => {
    if (!salesTrend.value) return { labels: [], datasets: [] }
    
    return {
      labels: salesTrend.value.trend.map(t => t.date),
      datasets: [
        {
          label: t('app.reports.daily_sales'),
          data: salesTrend.value.trend.map(t => t.total_sales),
          borderColor: chartColors.primaryBorder,
          backgroundColor: 'rgba(79, 70, 229, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    }
  })

  return {
    topProductsChartData,
    paymentChartData,
    salesTrendChartData
  }
}
